/**
 * O Conferir por CCB declarada (E177.F5, GUS-253, ADR-0130; HTML 6.8 por CCB `?tela=conferencia-ccb`
 * e `conflito-ccb`):
 *
 * - **Conflito** — cada CCB com o identificador do pacote divergente e ainda sem decisão: "o pacote
 *   indica a CCB x", com "Corrigir número da CCB" (de volta aos contratos) e "Manter … e registrar
 *   pendência" (grava a decisão; a conclusão leva a pendência à trilha do caso da CCB). Nada é
 *   reatribuído.
 *
 * Os documentos por CCB ficam em `DocumentosPorCcb.tsx`.
 */
import { useState } from 'react';
import type { AxiosInstance } from 'axios';

import { Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { ContratoDoEnvioData } from '@/lib/abertura_do_caso';
import { manterDivergencia } from '@/lib/ccbs_da_abertura';

import { divergentesSemDecisao } from './conferenciaPorCcb';
import { AvisoDaAbertura } from './PecasDaAbertura';

const t = ptBR.abertura.conferirPorCcb;
const rotulo = (numero: string): string =>
  ptBR.abertura.contratosDigitais.rotulo.replace('{numero}', numero);

function ConflitoDaCcb({
  client,
  caseId,
  contrato,
  onCorrigir,
  onMantida,
  disabled,
}: {
  client: AxiosInstance;
  caseId: string;
  contrato: ContratoDoEnvioData;
  onCorrigir: () => void;
  onMantida: () => Promise<void>;
  disabled: boolean;
}): JSX.Element | null {
  const [mantendo, setMantendo] = useState(false);
  const [falhou, setFalhou] = useState(false);
  const ccb = contrato.ccb;
  const lida = ccb?.divergencia?.lida;
  if (ccb == null || lida === undefined) return null;
  const nome = rotulo(ccb.numero);

  async function manter(): Promise<void> {
    if (ccb == null) return;
    setMantendo(true);
    setFalhou(false);
    try {
      await manterDivergencia(client, caseId, ccb.id);
      await onMantida();
    } catch {
      setFalhou(true);
    } finally {
      setMantendo(false);
    }
  }

  return (
    <AvisoDaAbertura
      tom="aviso"
      titulo={t.conflitoTitulo.replace('{ccb}', nome).replace('{lido}', lida.valor)}
      texto={t.conflitoTexto}
      testId={`conferir-conflito-${ccb.id}`}
    >
      <p className="text-[13px] text-fg-muted">
        {t.conflitoFonte
          .replace('{fonte}', ptBR.abertura.conferirEnvio.fonte[lida.fonte])
          .replace('{valor}', lida.valor)}
      </p>
      <div className="mt-3 flex flex-wrap gap-3">
        <Button
          type="button"
          variant="secondary"
          onClick={onCorrigir}
          disabled={mantendo || disabled}
          data-testid={`conferir-corrigir-${ccb.id}`}
        >
          {t.corrigir}
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={() => void manter()}
          disabled={mantendo || disabled}
          data-testid={`conferir-manter-${ccb.id}`}
        >
          {mantendo ? t.mantendo : t.manter.replace('{ccb}', nome)}
        </Button>
      </div>
      {falhou ? (
        <p className="mt-2 text-[13px] text-danger" data-testid={`conferir-manter-erro-${ccb.id}`}>
          {t.erroAoManter}
        </p>
      ) : null}
    </AvisoDaAbertura>
  );
}

/** Os avisos de conflito das CCBs sem decisão, na ordem dos blocos. */
export function ConflitosDasCcbs(props: {
  client: AxiosInstance;
  caseId: string;
  contratos: readonly ContratoDoEnvioData[];
  onCorrigir: () => void;
  onMantida: () => Promise<void>;
  disabled: boolean;
}): JSX.Element | null {
  const divergentes = divergentesSemDecisao(props.contratos);
  if (divergentes.length === 0) return null;
  return (
    <div className="flex flex-col gap-3" data-testid="conferir-conflitos">
      {divergentes.map((c) => (
        <ConflitoDaCcb
          key={c.chave}
          client={props.client}
          caseId={props.caseId}
          contrato={c}
          onCorrigir={props.onCorrigir}
          onMantida={props.onMantida}
          disabled={props.disabled}
        />
      ))}
    </div>
  );
}
